// @flow
/** @jsx jsx */

import { Colors } from '@zapier/theme';
import { serviceUtils } from '@zapier/common-utils';
import { ClassNames, jsx } from '@emotion/core';

type LogoImageProps = {
  /**
   * The class that will be applied to the element. Usually this is the name of the service,
   * for example 'CodeAPI'. There is some backend magic that generates a .css file, maping
   * this class name to an image file on the CDN contain the service logo. The background-image
   * style will point to that image file.
   *
   * If the service isn't provided the logo will default to the grey Zapier placeholder logo.
   *
   * If the image for that service can't be found it will default to the grey Zapier placeholder
   * logo.
   */
  app: ?string,

  /** Background color */
  backgroundType?: 'clear' | 'white',

  /** Whether to add a border to the logo */
  border?: boolean,

  /** The radius of the border */
  borderRadius?: number,

  /** The padding (in pixels) to use around the logo when border is true */
  borderPadding?: number,

  /** The size of the logo in pixels. This sets the height and the width of the container.
   * Logos will be selected from the most appropriate size (16, 32, 64, 128) and scaled to fit.
   */
  size?: number,
};

// @TODO: Remove this hack once we figure out how to properly deprecate Python apps.
// Google Sheets was replaced and that caused the icon to disappear.
// So adding it back in via emergency hack.
// https://zapier.slack.com/archives/C03TB1UMA/p1490971184595755
const API_IMAGES = {
  AppTesterAPI:
    "url('https://cdn.zapier.com/storage/services/4401a6c0fdab0b69893ab957f2712b81.png')",
  BranchingAPI:
    "url('https://cdn.zapier.com/storage/services/137baf61724ea2c64812417382bddd1d.128x128.png')",
  CapsuleCRMAPI:
    "url('https://cdn.zapier.com/storage/services/1029d32eb05260b4a52ba6eebf5dc20f.png')",
  EventbriteAPI:
    "url('https://cdn.zapier.com/storage/photos/4b2b5f32f92c80fc304ab369be1da347.png')",
  EventbriteDevAPI:
    "url('https://cdn.zapier.com/storage/developer/638ebef07f1e312e20ee45ddb7df6be5.png')",
  GoogleContactsV2API:
    "url('https://cdn.zapier.com/storage/services/1508661b55cd3c5ad1787303b0f58c99.png')",
  GoogleSheetsAPI:
    "url('https://cdn.zapier.com/storage/services/e37713ab22d82f777575279db97cd805.128x128.png')",
  MailChimpAPI:
    "url('https://cdn.zapier.com/storage/services/5c727288d9c2f69a9eee136c5f5a0f72.128x128.png')",
  OneNoteDevAPI:
    "url('https://cdn.zapier.com/storage/developer/e951e0105790e43b117e04b7bba34601.png')",
  PivotalTrackerAPI:
    "url('https://cdn.zapier.com/storage/services/84172dde558de382873cf72a00c92818.128x128.png')",
  QuickBaseAPI:
    "url('https://cdn.zapier.com/storage/services/3d348ba36ddb1a656e434ccbb984e3c6.png')",
  SkyDriveAPI:
    "url('https://cdn.zapier.com/storage/services/e67867e9f6f5c089e243dad54467ad33.128x128.png')",
  SurveyMonkeyAPI:
    "url('https://cdn.zapier.com/storage/services/39b027caffb7fad05f73ed80d664a246.128x128.png')",
  TypeformDevAPI:
    "url('https://cdn.zapier.com/storage/developer/6df0e2a9ca7350c7407f0d9c2b0709c0.png')",
  ZapierInternalAPI:
    "url('https://cdn.zapier.com/storage/photos/ff7409c6a52dc217fe9829531c3a66dd.png')",
  ZohoCRMAPI:
    "url('https://images.zapier.com/storage/services/58719c2f59576a993b6c6855e0f585df.128x128.png')",
};

const LOGO_SIZES = [16, 32, 64, 128];

const logoSize = (size: number) => {
  return (
    LOGO_SIZES.find(value => value > size) || LOGO_SIZES[LOGO_SIZES.length - 1]
  );
};

const LogoImage = ({
  app,
  backgroundType = 'clear',
  border = false,
  borderPadding = 10,
  borderRadius = 20,
  size = 128,
}: LogoImageProps) => {
  const appUnversioned = serviceUtils.stripVersion(app);
  const imageSize = logoSize(size);
  const sizeXsize = `${imageSize}x${imageSize}`;
  const sizeWithPx = `${size}px`;
  const adjustedSizeWithPx = `${border ? size + borderPadding * 2 : size}px`;

  const styles = {
    // Base props
    backgroundPosition: 'center center',
    backgroundRepeat: 'no-repeat',
    display: 'inline-block',
    verticalAlign: 'middle',

    // Size props
    backgroundSize: `${sizeWithPx} ${sizeWithPx}`,
    minWidth: adjustedSizeWithPx,
    width: adjustedSizeWithPx,
    height: adjustedSizeWithPx,

    // Image for hardcoded apps
    backgroundImage: API_IMAGES[appUnversioned],

    backgroundColor: backgroundType === 'white' ? Colors.white : 'transparent',

    // Border props
    // This looks a bit weird because we can't use border && {...} due to flow complaining.
    // Also, can't just use the css function from emotion and conditionally add args either,
    // because the imported css function doesn't work with the ClassNames component used
    // below
    ...(border
      ? {
          border: `1px solid ${Colors.theme.secondary.border}`,
          borderRadius,
        }
      : {}),
  };

  const classes = [
    `PlaceholderAPI${sizeXsize}`,
    `${appUnversioned}${sizeXsize}`,
  ];

  // This is a bit hackey but we need to include the app as a class for the div.
  // Logo images are stored in the backend-generated files:
  //   - global-logos.css
  //   - private-logos.css
  return (
    <ClassNames>
      {({ css, cx }) => <div className={cx(css(styles), classes)} />}
    </ClassNames>
  );
};

export default LogoImage;
